import React from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, CardContent, Grid, Container, Chip } from "@mui/material";
import { keyframes } from "@mui/system";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TempleHinduIcon from "@mui/icons-material/TempleHindu";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Banner from "../../assets/Gpouram2.jpg";

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export default function BookPreview() {
    const { state } = useLocation();

    const getTamilMonth = () => {
        const tamilMonths = [
            "சித்திரை",
            "வைகாசி",
            "ஆனி",
            "ஆடி",
            "ஆவணி",
            "புரட்டாசி",
            "ஐப்பசி",
            "கார்த்திகை",
            "மார்கழி",
            "தை",
            "மாசி",
            "பங்குனி",
        ];

        const monthStartDates = [
            [3, 14], [4, 15], [5, 15], [6, 17], [7, 17], [8, 17],
            [9, 17], [10, 16], [11, 16], [0, 14], [1, 13], [2, 14],
        ];

        const today = new Date();
        const year = today.getFullYear();

        for (let i = 0; i < monthStartDates.length; i++) {
            const [month, day] = monthStartDates[i];
            const nextIndex = (i + 1) % 12;
            const nextMonth = monthStartDates[nextIndex];
            const startDate = new Date(year, month, day);
            const endDate = new Date(
                year + (nextIndex === 0 ? 1 : 0),
                nextMonth[0],
                nextMonth[1]
            );

            if (today >= startDate && today < endDate) {
                return tamilMonths[i];
            }
        }

        return tamilMonths[0];
    };

    const currentMonth = getTamilMonth();

    const poojaTimings = [
        { time: "5:30 AM", name: "சுவாமி பணிவிடை", icon: "🌅", color: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)" },
        { time: "12:00 PM", name: "உச்சிகால பணிவிடை", icon: "☀️", color: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)" },
        { time: "6:00 PM", name: "இரவு பணிவிடை", icon: "🌇", color: "linear-gradient(135deg, #ea580c 0%, #dc2626 100%)" },
    ];

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                background: "linear-gradient(180deg, #fffbeb 0%, #fff7ed 50%, #ffedd5 100%)",
            }}
        >
            {/* Banner Section with Overlay */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: "250px", sm: "350px", md: "450px" },
                    overflow: "hidden",
                }}
            >
                <img
                    src={Banner}
                    alt="Temple Banner"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "linear-gradient(180deg, rgba(217,119,6,0.3) 0%, rgba(217,119,6,0.7) 100%)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            animation: `${float} 3s ease-in-out infinite`,
                        }}
                    >
                        <TempleHinduIcon
                            sx={{
                                fontSize: { xs: 60, md: 80 },
                                color: "white",
                                mb: 2,
                                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h2"
                        sx={{
                            color: "white",
                            fontWeight: 700,
                            textAlign: "center",
                            fontFamily: "serif",
                            fontSize: { xs: "2rem", md: "3rem" },
                            textShadow: "3px 3px 6px rgba(0,0,0,0.4)",
                            mb: 1,
                            animation: `${fadeInUp} 1s ease-out`,
                        }}
                    >
                        இன்றைய பணிவிடைகள்
                    </Typography>
                    <Chip
                        label={`${currentMonth} மாதம்`}
                        sx={{
                            backgroundColor: "rgba(255,255,255,0.3)",
                            backdropFilter: "blur(10px)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "1rem",
                            px: 2,
                            border: "2px solid rgba(255,255,255,0.5)",
                            animation: `${pulse} 2s ease-in-out infinite`,
                        }}
                    />
                </Box>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
                {/* Decorative Top Element */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 4,
                    }}
                >
                    <Box
                        sx={{
                            width: "80px",
                            height: "4px",
                            background: "linear-gradient(90deg, transparent, #d97706, transparent)",
                            borderRadius: "2px",
                            animation: `${shimmer} 3s infinite linear`,
                            backgroundSize: "1000px 100%",
                        }}
                    />
                </Box>

                <Typography
                    variant="h4"
                    sx={{
                        textAlign: "center",
                        color: "#92400e",
                        fontWeight: 700,
                        mb: 2,
                        fontFamily: "serif",
                        fontSize: { xs: "1.75rem", md: "2.125rem" },
                    }}
                >
                    இன்று நடைபெறும் பணிவிடைகள்
                </Typography>

                <Typography
                    sx={{
                        textAlign: "center",
                        color: "#78350f",
                        mb: 6,
                        fontFamily: "serif",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                    }}
                >
                    அனைவரும் கலந்துகொண்டு ஆசி பெறுங்கள்
                </Typography>

                {/* Pooja Timing Cards */}
                <Grid container spacing={4} justifyContent="center">
                    {poojaTimings.map((pooja, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                sx={{
                                    background: pooja.color,
                                    borderRadius: "24px",
                                    boxShadow: "0 10px 40px rgba(217,119,6,0.2)",
                                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                    position: "relative",
                                    overflow: "visible",
                                    animation: `${fadeInUp} 0.6s ease-out ${index * 0.2}s both`,
                                    "&:hover": {
                                        transform: "translateY(-15px) scale(1.03)",
                                        boxShadow: "0 20px 60px rgba(217,119,6,0.4)",
                                    },
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: "-3px",
                                        left: "-3px",
                                        right: "-3px",
                                        bottom: "-3px",
                                        background: "linear-gradient(45deg, #fbbf24, #f59e0b, #fb923c, #ea580c)",
                                        borderRadius: "24px",
                                        zIndex: -1,
                                        opacity: 0,
                                        transition: "opacity 0.3s",
                                    },
                                    "&:hover::before": {
                                        opacity: 1,
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 4,
                                        textAlign: "center",
                                        position: "relative",
                                    }}
                                >
                                    {/* Icon with rotation animation */}
                                    <Box
                                        sx={{
                                            fontSize: "3.5rem",
                                            mb: 2,
                                            display: "inline-block",
                                            animation: `${rotate} 20s linear infinite`,
                                            "&:hover": {
                                                animation: `${rotate} 2s linear infinite`,
                                            },
                                        }}
                                    >
                                        {pooja.icon}
                                    </Box>

                                    {/* Time with icon */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            mb: 2,
                                            backgroundColor: "rgba(255,255,255,0.3)",
                                            backdropFilter: "blur(10px)",
                                            py: 1,
                                            px: 2,
                                            borderRadius: "20px",
                                            border: "2px solid rgba(255,255,255,0.5)",
                                        }}
                                    >
                                        <AccessTimeIcon sx={{ color: "white", fontSize: 20 }} />
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                color: "white",
                                                fontWeight: 700,
                                                fontFamily: "monospace",
                                            }}
                                        >
                                            {pooja.time}
                                        </Typography>
                                    </Box>

                                    {/* Pooja name */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: "white",
                                            fontWeight: 600,
                                            fontFamily: "serif",
                                            fontSize: "1.25rem",
                                            textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {pooja.name}
                                    </Typography>

                                    {/* Decorative element */}
                                    <Box
                                        sx={{
                                            mt: 3,
                                            height: "3px",
                                            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                                            borderRadius: "2px",
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Special Pooja Section */}
                <Box
                    sx={{
                        mt: 8,
                        position: "relative",
                        animation: `${fadeInUp} 0.8s ease-out 0.6s both`,
                    }}
                >
                    <Card
                        sx={{
                            background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
                            borderRadius: "24px",
                            border: "3px solid #fed7aa",
                            boxShadow: "0 10px 40px rgba(217,119,6,0.15)",
                            overflow: "hidden",
                            position: "relative",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "-100%",
                                width: "100%",
                                height: "100%",
                                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
                                animation: `${shimmer} 3s infinite`,
                            },
                        }}
                    >
                        <CardContent sx={{ p: { xs: 4, md: 5 }, textAlign: "center" }}>
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 3,
                                    backgroundColor: "#d97706",
                                    color: "white",
                                    px: 3,
                                    py: 1,
                                    borderRadius: "30px",
                                    boxShadow: "0 4px 15px rgba(217,119,6,0.3)",
                                    animation: `${float} 2s ease-in-out infinite`,
                                }}
                            >
                                <AutoAwesomeIcon />
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        fontFamily: "serif",
                                    }}
                                >
                                    சிறப்பு அறிவிப்பு
                                </Typography>
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{
                                    color: "#92400e",
                                    fontWeight: 600,
                                    mb: 2,
                                    fontFamily: "serif",
                                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                                }}
                            >
                                {currentMonth} மாத சிறப்பு பணிவிடைகள்
                            </Typography>

                            <Typography
                                sx={{
                                    color: "#78350f",
                                    fontFamily: "serif",
                                    fontSize: { xs: "1rem", md: "1.125rem" },
                                    lineHeight: 1.8,
                                    maxWidth: "800px",
                                    mx: "auto",
                                }}
                            >
                                இந்த {currentMonth} மாதத்தின் முதல் ஞாயிற்றுக்கிழமை சிறப்பு பணிவிடைகள் நடைபெறும்.
                                <br />
                                அனைவரும் தவறாமல் கலந்துகொண்டு வைகுண்ட நாராயணரின் அருளாசி பெறுங்கள்.
                            </Typography>

                            {/* Decorative stars */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 2,
                                    mt: 3,
                                }}
                            >
                                {[0, 1, 2].map((i) => (
                                    <AutoAwesomeIcon
                                        key={i}
                                        sx={{
                                            color: "#fbbf24",
                                            fontSize: 24,
                                            animation: `${pulse} 1.5s ease-in-out infinite ${i * 0.3}s`,
                                        }}
                                    />
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Decorative Bottom Element */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 6,
                    }}
                >
                    <Box
                        sx={{
                            width: "80px",
                            height: "4px",
                            background: "linear-gradient(90deg, transparent, #d97706, transparent)",
                            borderRadius: "2px",
                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
}